import React, { useState } from "react";

import { changeInsurance } from "../../slices/actions";
import { toggleModal } from "../../slices/popupSlice";
import {
	insuranceAdded,
	insuranceRemoved,
	prepareInsuranceItem,
	trackPopupViewEvent,
} from "../../tracking/googleAnalytics";

import { useDispatch, useSelector } from "../../utils/hooks";
import strings from "../../utils/strings";
import { FlexInsuranceContent } from "../products/FlexInsuranceContent";
import { INSURANCE_MODE, INSURANCE_TYPE } from "../products/Insurance.type";
import { InsuranceContent } from "../products/InsuranceContent";

import "./Modal.scss";
import "../products/Insurance.scss";

export const InsuranceModal = () => {
	const dispatch = useDispatch();

	const cartId = useSelector(
		(state) => state.popup.products.mainProduct.cartId,
	);

	const cartItemId = useSelector(
		(state) => state.popup.products.mainProduct.cartItemId,
	);

	const insurance = useSelector(
		(state) => state.popup.products.mainProduct.insurance,
	);

	const selectedInsuranceId = useSelector(
		(state) => state.popup.products.mainProduct.selectedInsuranceId,
	);

	const redirectUrl = useSelector(
		(state) => state.popup.modal?.config?.redirectUrl,
	);

	const productId = useSelector(
		(state) => state.popup.products.mainProduct.insuranceProductId,
	);

	const productName = useSelector(
		(state) => state.popup.products.mainProduct.productName,
	);

	const isFlexInsurance = useSelector(
		(state) => state.popup.products.mainProduct.isFlexInsurance,
	);

	const [currentInsuranceId, setCurrentInsuranceId] = useState(
		selectedInsuranceId || insurance[0].id,
	);

	const chooseInsurance = ({ event, insurance, mode }) => {
		let id = null;
		if (mode === INSURANCE_MODE.REMOVE) {
			event.preventDefault();
			const element = document.getElementById(
				currentInsuranceId,
			) as HTMLInputElement;

			if (element) {
				element.checked = false;
			}
		} else {
			id = insurance.id;
		}

		setCurrentInsuranceId(id);

		return dispatch(
			changeInsurance({
				cartId,
				cartItemId,
				insuranceId: id,
			}),
		)
			.unwrap()
			.then((response) => response);
	};

	const handleChange = async (event, mode) => {
		event.preventDefault();
		const insuranceLine = !isFlexInsurance
			? insurance.find(
					(insuranceLine) => insuranceLine.id === currentInsuranceId,
				)
			: insurance[0];

		const response = await chooseInsurance({
			event,
			insurance: insuranceLine,
			mode,
		});

		const insuranceItem = prepareInsuranceItem({
			id: insuranceLine.id,
			name: insuranceLine.description,
			price: insuranceLine.price,
			variant: isFlexInsurance ? INSURANCE_TYPE.FLEX : INSURANCE_TYPE.REGULAR,
		});

		if (mode === INSURANCE_MODE.CHANGE) {
			insuranceAdded(insuranceItem);
		} else if (selectedInsuranceId) {
			insuranceRemoved(insuranceItem);
		}

		trackPopupViewEvent({
			category: "Information",
			name:
				mode === INSURANCE_MODE.CHANGE
					? "Insurance modal closed (insurance set)"
					: "Insurance modal closed (insurance removed)",
		});

		if (response && redirectUrl) {
			window.location.href = redirectUrl;
		}

		dispatch(toggleModal({ id: "", isOpen: false, config: null }));
	};

	if (!insurance || insurance.length === 0) {
		return null;
	}

	return (
		<div
			className="addtocartpopupModal--container"
			role="dialog"
			aria-live="polite"
			aria-modal="true"
			aria-labelledby="insurance__heading"
		>
			<div className="addtocartpopupModal--iconcontainer">
				<img
					data-testid="product-image"
					alt={productName}
					src={`/img/p/400/${productId}.jpg`}
					width="400"
					height="300"
					loading="lazy"
					decoding="async"
				/>
			</div>
			<div className="addtocartpopupModal--row">
				<section className="addtocart-insurance__container">
					<form
						className="addtocart-insurance"
						aria-labelledby="insurance__heading"
					>
						<div className="addtocart-insurance__info">
							<h3>
								{isFlexInsurance
									? strings["addtocartpopup.flex.insurance.title"]
									: strings["addtocartpopup.insurance.title"]}
							</h3>
							<h2
								className="addtocart-insurance__info--title"
								id="insurance__heading"
								dangerouslySetInnerHTML={{
									__html: isFlexInsurance
										? strings["addtocartpopup.flex.insurance.subtitle"]
										: strings["addtocartpopup.insurance.subtitle"],
								}}
							/>
							{isFlexInsurance ? (
								<FlexInsuranceContent />
							) : (
								<InsuranceContent />
							)}
						</div>

						<div className="addtocart-insurance__inputs">
							<fieldset>
								{insurance?.map((insuranceLine) => (
									<div
										className="addtocart-insurance__input-wrapper"
										key={insuranceLine.id}
									>
										<input
											type={isFlexInsurance ? "checkbox" : "radio"}
											id={insuranceLine.id}
											checked={insuranceLine.id === currentInsuranceId}
											data-testid={`insurance-option-input-${insuranceLine.id}`}
											name="insurance"
											value={insuranceLine.description}
											onChange={(event) => {
												chooseInsurance({
													event,
													insurance: insuranceLine,
													mode:
														insuranceLine.id === currentInsuranceId
															? INSURANCE_MODE.REMOVE
															: INSURANCE_MODE.CHANGE,
												});
											}}
										/>
										<label
											htmlFor={insuranceLine.id}
											data-testid={`insurance-option-label-${insuranceLine.id}`}
											className="addtocart-input__label"
										>
											{isFlexInsurance
												? insuranceLine.subscriptionDescription
												: insuranceLine.description}
											<span className="addtocart-input__label--price">
												{isFlexInsurance ? (
													<>
														{insuranceLine.monthlyPriceNet}
														{strings["subscription.price.permonth"]}
													</>
												) : (
													insuranceLine.priceNet
												)}
											</span>
										</label>
									</div>
								))}
							</fieldset>
						</div>

						<div className="addtocart-insurance__buttons caas">
							<button
								className="addtocart-button btn btn-medium tertiary-grey"
								data-testid="footer-button-continue"
								onClick={(event) => handleChange(event, INSURANCE_MODE.REMOVE)}
							>
								<span>
									{strings["addtocartpopup.insurance.modal.button.no"]}
								</span>
							</button>
							<button
								className="addtocart-button btn btn-medium primary"
								data-testid="footer-button-gotocart"
								onClick={(event) => handleChange(event, INSURANCE_MODE.CHANGE)}
							>
								<span>
									{strings["addtocartpopup.insurance.modal.button.yes"]}
								</span>
							</button>
						</div>
					</form>
				</section>
			</div>
		</div>
	);
};
